import Styles from './FontSize.module.scss'
import { ComboBox, ComboBoxProps } from 'components/ComboBox/ComboBox'
import { track } from '@tldraw/tldraw'
import { useUpdateStyle } from '../VisualEditor/hooks/useUpdateStyle'
import { Item } from '@react-stately/collections'
import { FontSizeStyle, IFontSizeStyle } from '../VisualEditor/StyleProps'
import { useEffect, useState } from 'react'
import { FontSizeProps } from './Types'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Minus } from 'almanac-ui/icons'
import { Plus1 } from 'blaze-ui/icons/24'

const sizes = [6, 8, 10, 12, 14, 16, 18, 21, 24, 28, 32, 36, 42, 48, 56, 64, 72, 80, 88, 96, 104, 120, 144]

export const FontSize = track(({ style, editor }: FontSizeProps) => {
  const [isSearching, setIsSearching] = useState(false)
  const updateStyle = useUpdateStyle(editor)
  const [value, setValue] = useState<string>()

  useEffect(() => {
    setValue(style.type === 'shared' ? style.value.toString() : undefined)
  }, [style])

  const updateEachBy = (delta: number) => {
    editor.batch(() => {
      if (editor.isIn('select')) {
        editor.getSelectedShapes().forEach((shape) => {
          const stylePropKey = editor.styleProps[shape.type].get(FontSizeStyle)

          if (!stylePropKey || !(stylePropKey in shape.props)) return

          const prevValue = (shape.props as { [key: string]: IFontSizeStyle })[stylePropKey]

          editor.updateShape({
            ...shape,
            props: {
              [stylePropKey]: prevValue + delta,
            },
          })
        })
      }
    })
  }

  const baseProps: Partial<ComboBoxProps> = {
    'aria-label': 'Font size',
    size: 'md',
    wrapperClassName: Styles.comboboxWrapper,
    contentsClassName: Styles.comboboxContents,
    inputClassName: Styles.comboboxInput,
    popoverClassName: Styles.popover,
  }

  if (!isSearching) {
    return (
      <div className={Styles.fontSize}>
        <IconButton
          variant="tertiary"
          onPress={() => {
            updateEachBy(-1)
          }}
          useChildClass
        >
          <Minus size={20} />
        </IconButton>
        <ComboBox
          {...baseProps}
          onFocus={() => setIsSearching(true)}
          inputValue={style.type === 'shared' ? style.value.toString() : '--'}
        >
          <Item>{style.type === 'shared' ? style.value.toString() : 'Mixed'}</Item>
        </ComboBox>
        <IconButton
          variant="tertiary"
          onPress={() => {
            updateEachBy(1)
          }}
          useChildClass
        >
          <Plus1 size={20} />
        </IconButton>
      </div>
    )
  }

  return (
    <div className={Styles.fontSize}>
      <IconButton
        variant="tertiary"
        onPress={() => {
          updateEachBy(-1)
        }}
        useChildClass
      >
        <Minus size={20} />
      </IconButton>
      <ComboBox
        {...baseProps}
        autoFocus
        placeholder="--"
        menuTrigger="focus"
        inputValue={value}
        allowsCustomValue
        onBlur={() => setIsSearching(false)}
        onInputChange={(str) => {
          const newValue = str.replace(/\D/g, '')
          setValue(newValue)
          if (newValue) {
            updateStyle(FontSizeStyle, parseInt(newValue, 10))
          }
        }}
        onSelectionChange={(size) => {
          updateStyle(FontSizeStyle, parseInt(size as string, 10))
        }}
      >
        {sizes.map((size) => (
          <Item key={size.toString()}>{size.toString()}</Item>
        ))}
      </ComboBox>
      <IconButton
        variant="tertiary"
        onPress={() => {
          updateEachBy(1)
        }}
        useChildClass
      >
        <Plus1 size={20} />
      </IconButton>
    </div>
  )
})
